import { DataType } from '@lmt/shared-types';
import { filter, get, isEmpty } from 'lodash';
import i18next from 'i18next';
import * as yup from 'yup';

export type ColumnData = {
  id: number | null;
  title: string;
  dataType: DataType;
  referenceDictionaryId: number | null;
  isUnique: boolean;
  required: boolean;
};
export type DictionaryData = {
  columns?: ColumnData[] | null;
  manualOrdering?: boolean | null;
};

declare module 'yup' {
  interface ArraySchema<
    TIn extends any[] | null | undefined,
    TContext,
    TDefault = undefined,
    TFlags extends yup.Flags = '',
  > {
    uniqueProperty(
      propertyPath: string,
      message?: string,
    ): ArraySchema<TIn, TContext, TDefault, TFlags>;
  }
}

yup.addMethod(yup.array, 'uniqueProperty', function (propertyPath, message) {
  return this.test('unique', '', function (list: any) {
    const errors: any = [];
    list.forEach((item: any, index: number) => {
      const propertyValue = get(item, propertyPath);
      if (
        propertyValue &&
        filter(list, [propertyPath, propertyValue]).length > 1
      ) {
        errors.push(
          this.createError({
            path: `${this.path}[${index}].${propertyPath}`,
            message,
          }),
        );
      }
    });
    if (!isEmpty(errors)) {
      throw new yup.ValidationError(errors);
    }

    return true;
  });
});

export const dictionaryDataYupSchema: yup.Schema<Partial<DictionaryData>> =
  yup.object({
    manualOrdering: yup.boolean().default(false),
    columns: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.number().nullable().default(null),
          title: yup
            .string()
            .required(() => i18next.t('translation:errors.required'))
            .default(''),
          dataType: yup.mixed().required() as any,
          isUnique: yup.boolean().default(false),
          referenceDictionaryId: yup.mixed().when('dataType', {
            is: DataType.Dictionary,
            then: (schema) =>
              schema
                .required(() => i18next.t('translation:errors.required'))
                .typeError(() => i18next.t('translation:errors.required')),
            otherwise: (schema) => schema.notRequired().nullable(),
          }),
          required: yup.boolean().default(false),
        }),
      )
      .defined()
      .uniqueProperty(
        'title',
        (
          i18next.t(
            'translation:custom_dictionary.configure.errors.duplicate_column_title',
          ) ?? 'Duplicate column title'
        )?.toString(),
      ) as any,
  });
